import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../utils/axios';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { Users, Search, Filter, CheckCircle, XCircle, Clock, Edit, Trash2, Upload, FileCheck, Loader2, FileSpreadsheet, X, AlertCircle, ChevronLeft, ChevronRight, Download, User, Award, Hash, MapPin, Phone, Mail, Calendar, CreditCard } from 'lucide-react';
import QRCode from 'react-qr-code';
import { Member } from '../types';
import { EditMemberModal } from '../components/EditMemberModal';
import ImageLightbox from '../components/ImageLightbox';

const LIMIT = 20;

const MembersPage: React.FC = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [voucherStatuses, setVoucherStatuses] = useState<Record<number, string | null>>({});
  const [uploadingVoucher, setUploadingVoucher] = useState(false);
  const voucherInputRef = useRef<HTMLInputElement>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState<{ success: number; failed: number; emailsQueued?: number; errors: { row: number; error: string; email?: string }[] } | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchMembers = useCallback(async (page: number, search: string, status: string) => {
    setLoading(true);
    try {
      const params: any = { page, limit: LIMIT };
      if (status !== 'all') params.status = status;
      if (search.trim()) params.search = search.trim();

      const response = await axios.get('/members', { params });
      const data = response.data;

      // Handle both response shapes
      const memberList: Member[] = data?.data?.data ?? data?.data?.members ?? data?.data ?? [];
      const paginationData = data?.data?.pagination ?? data?.pagination ?? {};

      setMembers(Array.isArray(memberList) ? memberList : []);
      setPagination({
        total: paginationData.total ?? memberList.length,
        totalPages: paginationData.totalPages ?? 1,
      });
    } catch {
      toast.error('Failed to load members');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers(currentPage, searchTerm, statusFilter);
  }, [currentPage, searchTerm, statusFilter, fetchMembers]);

  // Debounce search input
  const handleSearchInput = (value: string) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 400);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleStatusUpdate = async (memberId: number, newStatus: string) => {
    try {
      if (newStatus === 'approved') {
        await axios.put(`/members/${memberId}/approve`);
      } else if (newStatus === 'rejected') {
        await axios.put(`/members/${memberId}/reject`);
      } else {
        await axios.patch(`/members/${memberId}`, { status: newStatus });
      }
      toast.success(`Member ${newStatus} successfully!`);
      fetchMembers(currentPage, searchTerm, statusFilter);
      setSelectedMember(null);
    } catch {
      toast.error('Failed to update member status');
    }
  };

  const handleDeleteMember = async (memberId: number, memberName: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete ${memberName}?\n\nThis action cannot be undone and will permanently remove:\n- Member profile\n- All attendance records\n- All points and achievements\n\nType 'DELETE' in the next prompt to confirm.`
    );
    if (!confirmed) return;

    const confirmText = window.prompt('Type DELETE to confirm deletion:');
    if (confirmText !== 'DELETE') {
      toast.info('Deletion cancelled');
      return;
    }

    try {
      await axios.delete(`/members/${memberId}`);
      toast.success(`Member ${memberName} deleted successfully!`);
      fetchMembers(currentPage, searchTerm, statusFilter);
      setSelectedMember(null);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to delete member');
    }
  };

  const fetchVoucherStatus = async (memberId: number) => {
    try {
      const response = await axios.get(`/members/${memberId}/voucher`);
      setVoucherStatuses(prev => ({ ...prev, [memberId]: response.data.data?.voucher_url || null }));
    } catch {
      // no voucher yet
    }
  };

  const handleOpenMember = async (member: Member) => {
    setSelectedMember(member);
    if (member.id) fetchVoucherStatus(member.id as number);
    if (member.id) {
      setLoadingDetail(true);
      try {
        const res = await axios.get(`/members/${member.id}`);
        const full = res.data?.data ?? res.data;
        if (full) setSelectedMember(full);
      } catch {
        // keep existing list data
      } finally {
        setLoadingDetail(false);
      }
    }
  };

  const handleVoucherUpload = async (memberId: number, file: File) => {
    setUploadingVoucher(true);
    try {
      const formData = new FormData();
      formData.append('voucher', file);
      const response = await axios.post(`/members/${memberId}/voucher`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setVoucherStatuses(prev => ({ ...prev, [memberId]: response.data.data?.voucher_url }));
      toast.success('Voucher uploaded successfully!');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to upload voucher');
    } finally {
      setUploadingVoucher(false);
      if (voucherInputRef.current) voucherInputRef.current.value = '';
    }
  };

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvUploading(true);
    setCsvResult(null);
    try {
      const formData = new FormData();
      formData.append('csv', file);
      const response = await axios.post('/members/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const result = response.data.data;
      setCsvResult(result);
      if (result.success > 0) {
        toast.success(`${result.success} member(s) registered. Sending welcome emails in background.`);
        setCurrentPage(1);
        fetchMembers(1, searchTerm, statusFilter);
      }
      if (result.failed > 0) toast.warning(`${result.failed} row(s) failed — check the result below`);
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'CSV upload failed');
    } finally {
      setCsvUploading(false);
      if (csvInputRef.current) csvInputRef.current.value = '';
    }
  };

  const [exporting, setExporting] = useState(false);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const params: any = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm.trim()) params.search = searchTerm.trim();

      const response = await axios.get('/members/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `members_export_${date}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Members exported successfully!');
    } catch {
      toast.error('Failed to export members');
    } finally {
      setExporting(false);
    }
  };

  // Pagination helpers
  const getPageNumbers = () => {
    const { totalPages } = pagination;
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 4) return [1, 2, 3, 4, 5, '...', totalPages];
    if (currentPage >= totalPages - 3) return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-secondary-50 pb-8">
        <div className="max-w-7xl mx-auto px-4 py-8">

          {/* Header */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-secondary-900 mb-2">Members Management</h1>
              <p className="text-sm text-secondary-600">
                {pagination.total > 0 ? `${pagination.total.toLocaleString()} total members` : 'Manage and approve member registrations'}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
                style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                tabIndex={-1}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { if (!csvUploading) csvInputRef.current?.click(); }}
                  disabled={csvUploading}
                  className="flex items-center gap-2 px-4 py-2.5 bg-[#0D5128] hover:bg-green-800 disabled:opacity-60 text-white rounded-lg font-medium text-sm transition-colors"
                >
                  {csvUploading ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />}
                  {csvUploading ? 'Uploading...' : 'Bulk Upload CSV'}
                </button>
                <button
                  type="button"
                  onClick={handleExportCsv}
                  disabled={exporting}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white border border-[#0D5128] hover:bg-green-50 disabled:opacity-60 text-[#0D5128] rounded-lg font-medium text-sm transition-colors"
                >
                  {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                  {exporting ? 'Exporting...' : 'Export CSV'}
                </button>
              </div>
              <a
                href="data:text/csv;charset=utf-8,full_name,father_name,email,phone,cnic,gender,age,address,occupation,organization,qualification,marital_status,department,student_id,university,password"
                download="members_template.csv"
                className="text-xs text-center text-[#0D5128] hover:underline"
              >
                Download CSV template
              </a>
            </div>
          </div>

          {/* CSV Upload Result */}
          {csvResult && (
            <div className="mb-6 bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                  <FileSpreadsheet size={16} className="text-[#0D5128]" />
                  CSV Upload Result
                </h3>
                <button onClick={() => setCsvResult(null)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
              </div>
              <div className="flex gap-4 mb-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle size={16} className="text-green-600" />
                  <span className="text-green-700 font-medium">{csvResult.success} registered</span>
                </div>
                {csvResult.emailsQueued != null && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock size={16} className="text-blue-500" />
                    <span className="text-blue-700 font-medium">{csvResult.emailsQueued} emails queued</span>
                  </div>
                )}
                {csvResult.failed > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <XCircle size={16} className="text-red-500" />
                    <span className="text-red-600 font-medium">{csvResult.failed} failed</span>
                  </div>
                )}
              </div>
              {csvResult.errors.length > 0 && (
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {csvResult.errors.map((err, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-red-600 bg-red-50 px-3 py-1.5 rounded-lg">
                      <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                      <span>Row {err.row}{err.email ? ` (${err.email})` : ''}: {err.error}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { icon: <Users size={24} className="text-primary-600" />, value: pagination.total, label: 'Total Members' },
              { icon: <CheckCircle size={24} className="text-green-600" />, value: members.filter(m => m.status === 'approved').length, label: 'Approved (this page)' },
              { icon: <Clock size={24} className="text-amber-600" />, value: members.filter(m => m.status === 'pending').length, label: 'Pending (this page)' },
              { icon: <XCircle size={24} className="text-red-600" />, value: members.filter(m => m.status === 'rejected').length, label: 'Rejected (this page)' },
            ].map((stat, i) => (
              <div key={i} className="card flex items-center gap-4">
                {stat.icon}
                <div>
                  <div className="text-2xl font-bold text-secondary-900">{stat.value.toLocaleString()}</div>
                  <div className="text-xs text-secondary-600">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-6 flex-wrap">
            <div className="flex-1 min-w-[250px] flex items-center gap-3 px-4 py-3 bg-white rounded-lg shadow-sm">
              <Search size={20} className="text-secondary-400" />
              <input
                type="text"
                placeholder="Search by name, email, member ID, phone, CNIC..."
                value={searchInput}
                onChange={(e) => handleSearchInput(e.target.value)}
                className="flex-1 border-0 outline-none text-sm"
              />
              {searchInput && (
                <button onClick={() => { setSearchInput(''); setSearchTerm(''); setCurrentPage(1); }}>
                  <X size={16} className="text-secondary-400 hover:text-secondary-600" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-3 px-4 py-3 bg-white rounded-lg shadow-sm">
              <Filter size={20} className="text-secondary-600" />
              <select
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="border-0 outline-none text-sm cursor-pointer bg-transparent"
              >
                <option value="all">All Status</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          {/* Members Table */}
          <div className="card overflow-hidden">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4 text-secondary-600">
                <div className="spinner"></div>
                <p>Loading members...</p>
              </div>
            ) : members.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-secondary-50 border-b-2 border-secondary-200">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">Member ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">Department</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">Points</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">Voucher</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => (
                      <tr key={member.id} className="border-b border-secondary-100 hover:bg-secondary-50 transition-colors">
                        <td className="px-4 py-3 text-sm">
                          <span className="px-3 py-1 bg-primary-100 text-primary-600 rounded text-xs font-semibold font-mono">
                            {member.member_id}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-secondary-700">{member.full_name}</td>
                        <td className="px-4 py-3 text-sm text-secondary-700">{member.email}</td>
                        <td className="px-4 py-3 text-sm text-secondary-700">{member.department ?? '—'}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`badge ${
                            member.status === 'approved' ? 'badge-success' :
                            member.status === 'pending' ? 'badge-warning' : 'badge-error'
                          }`}>
                            {member.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className="px-2 py-1 bg-secondary-100 text-secondary-700 rounded text-xs font-semibold">
                            {member.total_points ?? 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {voucherStatuses[member.id as number] ? (
                            <span className="inline-flex items-center gap-1 text-xs text-[#0D5128] font-medium">
                              <FileCheck size={14} /> Uploaded
                            </span>
                          ) : (
                            <span className="text-xs text-secondary-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex gap-2">
                            <button onClick={() => handleOpenMember(member)} className="btn btn-primary !py-2 !px-4 text-xs">
                              View
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteMember(member.id as number, member.full_name || 'this member'); }}
                              className="btn bg-red-600 text-white hover:bg-red-700 !py-2 !px-3 text-xs flex items-center gap-1"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 gap-4 text-secondary-500">
                <Users size={48} className="text-secondary-300" />
                <p>No members found</p>
              </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-4 border-t border-secondary-200 bg-white flex-wrap gap-3">
                <p className="text-sm text-secondary-600">
                  Showing {((currentPage - 1) * LIMIT) + 1}–{Math.min(currentPage * LIMIT, pagination.total)} of{' '}
                  <span className="font-semibold">{pagination.total.toLocaleString()}</span> members
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg hover:bg-secondary-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={18} />
                  </button>

                  {getPageNumbers().map((page, i) =>
                    page === '...' ? (
                      <span key={`ellipsis-${i}`} className="px-2 text-secondary-400">…</span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page as number)}
                        className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-[#0D5128] text-white'
                            : 'hover:bg-secondary-100 text-secondary-700'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}
                    disabled={currentPage === pagination.totalPages}
                    className="p-2 rounded-lg hover:bg-secondary-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Member Detail Modal */}
      {selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={() => setSelectedMember(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-200">
              <h2 className="text-xl font-bold text-secondary-900">Member Details</h2>
              <button onClick={() => setSelectedMember(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-secondary-100 text-secondary-500 hover:text-secondary-900 transition-colors text-xl leading-none">×</button>
            </div>

            <div className="p-6">
              {loadingDetail && (
                <div className="flex justify-center mb-4">
                  <Loader2 size={20} className="animate-spin text-[#0D5128]" />
                </div>
              )}

              {/* Profile header: image + name + status */}
              <div className="flex items-center gap-5 mb-6 p-4 bg-secondary-50 rounded-xl">
                <div className="relative flex-shrink-0">
                  {(selectedMember.profile_image || selectedMember.profileImage) ? (
                    <img
                      src={selectedMember.profile_image || selectedMember.profileImage}
                      alt={selectedMember.full_name}
                      onClick={() => setLightboxSrc(selectedMember.profile_image || selectedMember.profileImage || null)}
                      className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-md cursor-zoom-in"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-[#0D5128]/10 border-4 border-white shadow-md flex items-center justify-center">
                      <span className="text-2xl font-bold text-[#0D5128]">
                        {selectedMember.full_name?.charAt(0)?.toUpperCase() ?? <User size={28} />}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-secondary-900 truncate">{selectedMember.full_name}</h3>
                  <p className="text-sm text-secondary-500 truncate">{selectedMember.email}</p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
                      selectedMember.status === 'approved' ? 'bg-green-100 text-green-700' :
                      selectedMember.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {selectedMember.status === 'approved' ? <CheckCircle size={11} /> :
                       selectedMember.status === 'rejected' ? <XCircle size={11} /> : <Clock size={11} />}
                      {selectedMember.status?.charAt(0).toUpperCase() + selectedMember.status?.slice(1)}
                    </span>
                    {selectedMember.member_id && (
                      <span className="text-xs bg-secondary-200 text-secondary-700 px-2.5 py-1 rounded-full font-mono">
                        {selectedMember.member_id}
                      </span>
                    )}
                    {selectedMember.achievement_level && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full">
                        <Award size={11} /> {selectedMember.achievement_level}
                      </span>
                    )}
                  </div>
                </div>
                {/* QR Code */}
                {selectedMember.qr_code && selectedMember.qr_code.length < 2000 && (
                  <div className="flex-shrink-0 p-2 bg-white rounded-xl shadow border border-secondary-100">
                    <QRCode value={selectedMember.qr_code} size={80} />
                  </div>
                )}
              </div>

              {/* Details grid */}
              <div className="space-y-4 mb-6">

                {/* Personal Info */}
                <div>
                  <p className="text-xs font-bold text-[#0D5128] uppercase tracking-widest mb-2">Personal Information</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { icon: <Mail size={13} />, label: 'Email', value: selectedMember.email },
                      { icon: <Phone size={13} />, label: 'Phone', value: selectedMember.phone || '—' },
                      { icon: <CreditCard size={13} />, label: 'CNIC', value: selectedMember.cnic || '—' },
                      { icon: <User size={13} />, label: "Father's Name", value: selectedMember.father_name || '—' },
                      { icon: <User size={13} />, label: 'Gender', value: selectedMember.gender ? selectedMember.gender.charAt(0).toUpperCase() + selectedMember.gender.slice(1) : '—' },
                      { icon: <User size={13} />, label: 'Age', value: selectedMember.age ?? '—' },
                      { icon: <User size={13} />, label: 'Marital Status', value: selectedMember.marital_status ? selectedMember.marital_status.charAt(0).toUpperCase() + selectedMember.marital_status.slice(1) : '—' },
                      { icon: <Calendar size={13} />, label: 'Registered', value: selectedMember.registration_date ? new Date(selectedMember.registration_date).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : selectedMember.created_at ? new Date(selectedMember.created_at).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—' },
                    ].map((item, idx) => (
                      <div key={idx} className="bg-secondary-50 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-secondary-500 uppercase tracking-wide mb-1">
                          <span className="text-[#0D5128]">{item.icon}</span>
                          {item.label}
                        </div>
                        <div className="text-sm font-medium text-secondary-900 break-all">{String(item.value)}</div>
                      </div>
                    ))}
                    {selectedMember.address && (
                      <div className="col-span-2 bg-secondary-50 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-secondary-500 uppercase tracking-wide mb-1">
                          <span className="text-[#0D5128]"><MapPin size={13} /></span>
                          Residential Address
                        </div>
                        <div className="text-sm font-medium text-secondary-900">{selectedMember.address}{selectedMember.city ? `, ${selectedMember.city}` : ''}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Professional Info */}
                <div>
                  <p className="text-xs font-bold text-[#0D5128] uppercase tracking-widest mb-2">Professional / Educational</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { icon: <Hash size={13} />, label: 'Occupation', value: selectedMember.occupation || '—' },
                      { icon: <Hash size={13} />, label: 'Organization', value: selectedMember.organization || '—' },
                      { icon: <Hash size={13} />, label: 'Qualification', value: selectedMember.qualification || '—' },
                      { icon: <Hash size={13} />, label: 'Student ID', value: selectedMember.student_id || '—' },
                      { icon: <Users size={13} />, label: 'Department', value: selectedMember.department || '—' },
                      { icon: <Users size={13} />, label: 'University', value: selectedMember.university || '—' },
                    ].map((item, idx) => (
                      <div key={idx} className="bg-secondary-50 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-secondary-500 uppercase tracking-wide mb-1">
                          <span className="text-[#0D5128]">{item.icon}</span>
                          {item.label}
                        </div>
                        <div className="text-sm font-medium text-secondary-900 break-all">{String(item.value)}</div>
                      </div>
                    ))}
                    {selectedMember.areas_of_interest && selectedMember.areas_of_interest.length > 0 && (
                      <div className="col-span-2 bg-secondary-50 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-secondary-500 uppercase tracking-wide mb-2">
                          <span className="text-[#0D5128]"><Hash size={13} /></span>
                          Areas of Interest
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedMember.areas_of_interest.map((area, i) => (
                            <span key={i} className="px-2.5 py-1 bg-[#0D5128]/10 text-[#0D5128] text-xs font-medium rounded-full capitalize">{area}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Emergency Contact */}
                {(selectedMember.emergency_contact_name || selectedMember.emergency_contact_phone) && (
                  <div>
                    <p className="text-xs font-bold text-[#0D5128] uppercase tracking-widest mb-2">Emergency Contact</p>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { icon: <User size={13} />, label: 'Name', value: selectedMember.emergency_contact_name || '—' },
                        { icon: <User size={13} />, label: 'Relationship', value: selectedMember.emergency_contact_relationship ? selectedMember.emergency_contact_relationship.charAt(0).toUpperCase() + selectedMember.emergency_contact_relationship.slice(1) : '—' },
                        { icon: <Phone size={13} />, label: 'Phone', value: selectedMember.emergency_contact_phone || '—' },
                      ].map((item, idx) => (
                        <div key={idx} className="bg-secondary-50 rounded-lg p-3">
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-secondary-500 uppercase tracking-wide mb-1">
                            <span className="text-[#0D5128]">{item.icon}</span>
                            {item.label}
                          </div>
                          <div className="text-sm font-medium text-secondary-900 break-all">{String(item.value)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Portal Stats */}
                <div>
                  <p className="text-xs font-bold text-[#0D5128] uppercase tracking-widest mb-2">Portal Stats</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { icon: <Award size={13} />, label: 'Total Points', value: selectedMember.total_points ?? 0 },
                      { icon: <Hash size={13} />, label: 'Rank', value: selectedMember.rank_position ? `#${selectedMember.rank_position}` : '—' },
                      { icon: <Calendar size={13} />, label: 'Last Updated', value: selectedMember.updated_at ? new Date(selectedMember.updated_at).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—' },
                    ].map((item, idx) => (
                      <div key={idx} className="bg-secondary-50 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-secondary-500 uppercase tracking-wide mb-1">
                          <span className="text-[#0D5128]">{item.icon}</span>
                          {item.label}
                        </div>
                        <div className="text-sm font-medium text-secondary-900 break-all">{String(item.value)}</div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>

              {/* Voucher Upload */}
              <div className="mb-6 p-4 bg-secondary-50 rounded-xl border border-secondary-200">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-secondary-900">Member Voucher</p>
                    <p className="text-xs text-secondary-500 mt-0.5">Upload a voucher/card file for this member</p>
                  </div>
                  {selectedMember.id && voucherStatuses[selectedMember.id as number] ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0D5128] bg-[#0D5128]/10 px-2.5 py-1 rounded-full">
                      <FileCheck size={12} /> Voucher uploaded
                    </span>
                  ) : (
                    <span className="text-xs text-secondary-400 bg-secondary-200 px-2.5 py-1 rounded-full">No voucher</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <input
                    ref={voucherInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file && selectedMember.id) handleVoucherUpload(selectedMember.id as number, file);
                    }}
                  />
                  <button
                    onClick={() => voucherInputRef.current?.click()}
                    disabled={uploadingVoucher}
                    className="flex items-center gap-2 px-4 py-2 bg-[#0D5128] hover:bg-[#0a3e1f] text-white text-sm font-medium rounded-lg transition-all disabled:opacity-60"
                  >
                    {uploadingVoucher ? <><Loader2 size={16} className="animate-spin" /> Uploading...</> : <><Upload size={16} /> {selectedMember.id && voucherStatuses[selectedMember.id as number] ? 'Replace Voucher' : 'Upload Voucher'}</>}
                  </button>
                  <span className="text-xs text-secondary-400">PDF, JPG, PNG — max 10MB</span>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3 justify-center flex-wrap">
                <button onClick={() => { setEditingMember(selectedMember); setSelectedMember(null); }} className="btn bg-[#0D5128] text-white hover:bg-[#0a3e1f] flex items-center gap-2">
                  <Edit size={20} /> Edit Details
                </button>
                {selectedMember.status === 'pending' && (
                  <>
                    <button onClick={() => handleStatusUpdate(selectedMember.id as number, 'approved')} className="btn btn-primary">
                      <CheckCircle size={20} /> Approve
                    </button>
                    <button onClick={() => handleStatusUpdate(selectedMember.id as number, 'rejected')} className="btn bg-red-600 text-white hover:bg-red-700">
                      <XCircle size={20} /> Reject
                    </button>
                  </>
                )}
                <button onClick={() => handleDeleteMember(selectedMember.id as number, selectedMember.full_name || 'this member')} className="btn bg-red-700 text-white hover:bg-red-800 flex items-center gap-2 border-2 border-red-900">
                  <Trash2 size={20} /> Delete Member
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {editingMember && (
        <EditMemberModal
          member={editingMember}
          onClose={() => setEditingMember(null)}
          onUpdate={() => { fetchMembers(currentPage, searchTerm, statusFilter); setEditingMember(null); }}
        />
      )}
      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
    </>
  );
};

export default MembersPage;
